import { useEffect } from 'react';
import { domEventService } from '@/common/services/domEventService';

interface ChatWidgetCommandHandlerProps {
  onOpenChat: (context?: string) => void;
  onSetContext: (context: string) => void;
  onLoginChangeState: (loginState: { priorLoginState: string; newLoginState: string }) => void;
  onMinimizeChat: () => void;
  onCloseChat: () => void;
  onStartLivechat: () => void;
  onSendMessage: (message: string, options?: { autoPost?: boolean }) => void;
}

/**
 * Component responsible for handling external commands for the ChatWidget
 * Handles chatbotOpen and chatbotSetContext events with proper context management
 */
export const ChatWidgetCommandHandler = ({ 
  onOpenChat, 
  onSetContext,
  onLoginChangeState,
  onMinimizeChat,
  onCloseChat,
  onStartLivechat,
  onSendMessage
}: ChatWidgetCommandHandlerProps) => {
  
  useEffect(() => {
    const instanceId = Math.random().toString(36).substring(7);
    console.log(`[ChatWidgetCommandHandler ${instanceId}] Registering event listeners`);

    const handleChatbotOpenEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const context = customEvent.detail?.context;
      console.debug(`[ChatbotWidget ${instanceId}] chatbotOpen event handled`, { context });
      if (context !== undefined) {
        onOpenChat(context);
      } else {
        onOpenChat();
      }
    };

    const handleChatbotSetContextEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const context = customEvent.detail?.context || "";
      onSetContext(context);
      domEventService.emitEvent('chatbotContextSet', { context });
      console.debug('[ChatbotWidget] chatbotSetContext event handled', { context });
    };

    const handleChangeLoginStateEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const priorLoginState = customEvent.detail?.priorLoginState;
      const newLoginState = customEvent.detail?.newLoginState;

      if (priorLoginState === newLoginState) {
        console.debug('[ChatbotWidget] changeLoginState event ignored, no state change', { priorLoginState, newLoginState });
        return;
      }

      onLoginChangeState(customEvent.detail);
      domEventService.emitEvent("chatbotLoginStateChanged", { priorLoginState, newLoginState });

      console.debug('[ChatbotWidget] changeLoginState event handled', { priorLoginState, newLoginState });
    }

    const handleChatbotMinimizeEvent = () => {
      onMinimizeChat();
      console.debug('[ChatbotWidget] chatbotMinimize event handled');
    };

    const handleChatbotCloseEvent = () => {
      onCloseChat();
      console.debug('[ChatbotWidget] chatbotClose event handled');
    };

    const handleChatbotStartLivechatEvent = () => {
      onStartLivechat();
      console.debug('[ChatbotWidget] chatbotStartLivechatRequested event handled');
    };

    const handleChatbotSendMessageEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const message = customEvent.detail?.message;
      const options = customEvent.detail?.options;
      if (message) {
        onSendMessage(message, options);
        console.debug('[ChatbotWidget] chatbotSendMessage event handled', { message, options });
      }
    };

    const eventHandlers = {
      'chatbotOpen': handleChatbotOpenEvent,
      'chatbotSetContext': handleChatbotSetContextEvent,
      'chatbotMinimize': handleChatbotMinimizeEvent,
      'chatbotClose': handleChatbotCloseEvent,
      'chatbotStartLivechatRequested': handleChatbotStartLivechatEvent,
      'chatbotSendMessage': handleChatbotSendMessageEvent,
      'changeLoginState': handleChangeLoginStateEvent,
    };

    Object.entries(eventHandlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    return () => {
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, []);

  return null;
};

export default ChatWidgetCommandHandler;
